import { FC, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import { useI18n } from "../../hooks/useTranslations";
import { useClientsApi } from "../../http/clients/api";
import { SearchClient } from "../../http/clients/types";
import { PaginationQuery } from "../../types";
import SvgAdd from "../icons/Add";
import Button from "../ui/Button";
import Header from "../ui/Header";
import { Action, getDeleteAction, getEditAction } from "../ui/ListItemMenu";
import Panel from "../ui/Panel";
import Table from "../ui/Table";
import DeleteAdminClientDialog from "./DeleteAdminClientDialog";

const AdminClients: FC = () => {
  const i18n = useI18n();
  const filters = useListFilters({ isSearchable: true });
  const aborter = useRef(new AbortController());
  const [clientToDelete, setClientToDelete] = useState<SearchClient>();
  const clinetsApi = useClientsApi();
  const navigate = useNavigate();
  const userSettings = useUserSettings();

  const debouncedValue = useDebounce(filters.state.q);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const adminClientsSwr = clinetsApi.useAdminClients(query, aborter.current.signal);
  const [adminClients] = useDataInitializer(adminClientsSwr.data, adminClientsSwr.error);

  const getRowActions = (item: SearchClient): Action[] => {
    const actions: Action[] = [];

    if (userSettings.permissions.clients.update) {
      actions.push(getEditAction(i18n, () => navigate(`/admin/clients/${item.id}`)));
    }
    if (userSettings.permissions.clients.delete) {
      actions.push(getDeleteAction(i18n, () => setClientToDelete(item), true, !!item.secrets));
    }

    return actions;
  };

  return (
    <>
      <Header title={i18n.t.clients.plural} />
      <Panel>
        <Table<SearchClient>
          filters={filters}
          isTall
          buttons={
            userSettings.permissions.clients.insert
              ? {
                  main: (
                    <Button
                      glyph={SvgAdd}
                      buttonProps={{
                        onClick: () => navigate(`/admin/clients/new`),
                      }}
                    />
                  ),
                }
              : undefined
          }
          searchOptions={{
            placeholder: i18n.t.clients.search,
            onChange: () => {
              aborter.current.abort();
              aborter.current = new AbortController();
            },
          }}
          head={
            <>
              <th align="left">{i18n.t.common.name}</th>
              <th align="left">{i18n.t.secrets.plural}</th>
              {(userSettings.permissions.clients.delete || userSettings.permissions.clients.update) && <th></th>}
            </>
          }
          items={adminClients?.items}
          renderRow={(client) => (
            <>
              <td>
                <Link to={client.id}>{client.name}</Link>
              </td>
              <td>{client.secrets}</td>
            </>
          )}
          rowActions={getRowActions}
          total={adminClients?.total}
          error={adminClientsSwr.error}
        />
      </Panel>

      {!!clientToDelete && userSettings.permissions.clients.delete && (
        <DeleteAdminClientDialog client={clientToDelete} onClose={() => setClientToDelete(undefined)} />
      )}
    </>
  );
};

export default AdminClients;
